import { useEffect, useMemo, useState } from "react";
import { Check, CircleCheck, Download, Link2, Loader2, Pencil, Plus, RefreshCw, Send, Trash2, X } from "lucide-react";
import { listCompanies } from "../api/companies";
import { approveCompetitor, deleteCompetitor } from "../api/competitors";
import { downloadHotelMappingCsv, getHotelMapping, listHotels, recheckHotelMapping } from "../api/hotels";
import {
  manageHotels,
  ParsingManageDepth,
  ParsingManageGuests,
  ParsingManageHotelItem,
  ParsingManageHotelUrls,
  ParsingManageInterval,
  ParsingManageSetting,
} from "../api/parsing";
import EditMappingModal, { EDITABLE_PLATFORM_FIELDS, EditablePlatformKey } from "../components/modals/EditMappingModal";
import Topbar from "../components/Topbar";
import SelectField from "../components/controls/SelectField";
import { SCompanyRow } from "../types/companies";
import { HotelMappingRow, HotelOut } from "../types/hotels";

const PLATFORM_COLUMNS = EDITABLE_PLATFORM_FIELDS;
const PARSING_SETTINGS_EXISTING_GROUP_MESSAGE =
  "Для изменения настроек уже существующий группы используйте метод /parsing/parsing_settings";
const DEPTH_OPTIONS: ParsingManageDepth[] = [30, 90, 120, 180];
const INTERVAL_OPTIONS: Array<{ value: ParsingManageInterval; label: string }> = [
  { value: 1, label: "Один раз в месяц" },
  { value: 4, label: "Один раз в неделю" },
  { value: 30, label: "Один раз в день" },
  { value: 60, label: "Два раза в день" },
  { value: 90, label: "Три раза в день" },
  { value: 120, label: "Четыре раза в день" },
];
const GUEST_OPTIONS: ParsingManageGuests[] = [1, 2, 4];
const DEFAULT_PARSING_SETTING: ParsingManageSetting = {
  min_stay: 1,
  depth: 90,
  interval: 30,
  guests: 2,
};

function normalizePlatform(value: string) {
  return value.trim().toLowerCase();
}

function resolveParsingUrlKey(platform: string): keyof ParsingManageHotelUrls | null {
  const normalized = normalizePlatform(platform);
  const match = EDITABLE_PLATFORM_FIELDS.find((field) => {
    if (field.key === "official_website") return normalized === "travelline" || normalized === "official_website";
    if (field.key === "t_travel") return normalized === "tinkoff" || normalized === "t_travel";
    if (field.key === "sutochno") return normalized === "sutochno" || normalized === "sutochno.ru";
    return normalized === field.key;
  });
  return match?.key ?? null;
}

function inferParsingUrlKeyFromUrl(url?: string | null): keyof ParsingManageHotelUrls | null {
  if (!url) return null;

  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname.includes("ostrovok")) return "ostrovok";
    if (hostname.includes("tbank") || hostname.includes("tinkoff")) return "t_travel";
    if (hostname.includes("yandex")) return "yandex_travel";
    if (hostname.includes("sutochno")) return "sutochno";
    if (hostname.includes("alean")) return "alean";
    return "official_website";
  } catch {
    return "official_website";
  }
}

function buildParsingUrls(row: HotelMappingRow): ParsingManageHotelUrls {
  const urls: ParsingManageHotelUrls = {};

  for (const item of row.result) {
    const key = resolveParsingUrlKey(item.platform);
    if (key && item.url) {
      urls[key] = item.url;
    }
  }

  const fallbackKey = inferParsingUrlKeyFromUrl(row.url);
  if (fallbackKey && row.url && !urls[fallbackKey]) {
    urls[fallbackKey] = row.url;
  }

  return urls;
}

function getRowChangedPlatforms(row: HotelMappingRow): EditablePlatformKey[] {
  return Array.from(
    new Set(
      row.result
        .filter((item) => item.is_changed)
        .map((item) => resolveParsingUrlKey(item.platform))
        .filter((value): value is EditablePlatformKey => Boolean(value)),
    ),
  );
}

function resolveMainUid(row: HotelMappingRow) {
  return row.main_uid != null ? String(row.main_uid) : null;
}

function formatParsingErrorItem(item: unknown) {
  if (typeof item === "string") return item;

  if (item && typeof item === "object") {
    const error = item as {
      code?: unknown;
      description?: unknown;
      detail?: unknown;
      message?: unknown;
      error?: unknown;
    };
    if (error.code === "parsing_settings_existing_group") {
      return PARSING_SETTINGS_EXISTING_GROUP_MESSAGE;
    }
    if (typeof error.description === "string" && error.description.trim()) return error.description;
    if (typeof error.detail === "string" && error.detail.trim()) return error.detail;
    if (typeof error.message === "string" && error.message.trim()) return error.message;
    if (typeof error.error === "string" && error.error.trim()) return error.error;
  }

  try {
    return JSON.stringify(item);
  } catch {
    return String(item);
  }
}

function formatParsingErrors(errors: unknown[] | null | undefined) {
  if (!Array.isArray(errors) || errors.length === 0) return "";

  return errors.map(formatParsingErrorItem).join("; ");
}

function formatManageHotelsError(error: any, fallback: string) {
  const data = error?.response?.data ?? error;
  const formattedErrors = formatParsingErrors(data?.errors);
  if (formattedErrors) return formattedErrors;

  const detail = data?.detail ?? data?.error ?? data?.message;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail != null) {
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }

  return fallback;
}

function hasExistingParsing(hotel: HotelOut | null) {
  if (!hotel) return false;
  return hotel.parsing_status === 1 || (hotel.parsing_count ?? 0) > 0 || (hotel.parsing?.length ?? 0) > 0;
}

export default function MappingPage() {
  const [parsingMode, setParsingMode] = useState<"all" | "approved">("all");
  const [company, setCompany] = useState("");
  const [object, setObject] = useState("");
  const [companies, setCompanies] = useState<SCompanyRow[]>([]);
  const [hotels, setHotels] = useState<HotelOut[]>([]);
  const [mappingRows, setMappingRows] = useState<HotelMappingRow[]>([]);
  const [editedUrlsByRow, setEditedUrlsByRow] = useState<Record<number, Partial<Record<EditablePlatformKey, string | null>>>>({});
  const [changedPlatformsByRow, setChangedPlatformsByRow] = useState<Record<number, EditablePlatformKey[]>>({});
  const [editingRow, setEditingRow] = useState<HotelMappingRow | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [parsingSettings, setParsingSettings] = useState<ParsingManageSetting[]>([]);

  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<"approve" | "delete" | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [parsingLoading, setParsingLoading] = useState(false);
  const [parsingMessage, setParsingMessage] = useState("");

  const [companiesError, setCompaniesError] = useState("");
  const [hotelsError, setHotelsError] = useState("");
  const [mappingError, setMappingError] = useState("");

  useEffect(() => {
    (async () => {
      setCompaniesLoading(true);
      setCompaniesError("");
      try {
        const data = await listCompanies({ page: 1, per_page: 200 });
        setCompanies(data.items);
      } catch (error) {
        console.error("Не удалось загрузить компании", error);
        setCompaniesError("Не удалось загрузить компании");
      } finally {
        setCompaniesLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!company) {
      setHotels([]);
      setObject("");
      setMappingRows([]);
      setParsingMode("all");
      setEditedUrlsByRow({});
      setChangedPlatformsByRow({});
      setEditingRow(null);
      setEditModalOpen(false);
      setParsingSettings([]);
      setHotelsError("");
      setMappingError("");
      setParsingMessage("");
      return;
    }

    (async () => {
      setHotelsLoading(true);
      setHotelsError("");
      try {
        const data = await listHotels(Number(company), { page: 1, per_page: 200 });
        setHotels(data.items);
      } catch (error) {
        console.error("Не удалось загрузить объекты размещения", error);
        setHotelsError("Не удалось загрузить объекты размещения");
      } finally {
        setHotelsLoading(false);
      }
    })();
  }, [company]);

  async function loadMapping(hotelId: string) {
    if (!hotelId) {
      setMappingRows([]);
      setParsingMode("all");
      setEditedUrlsByRow({});
      setChangedPlatformsByRow({});
      setEditingRow(null);
      setEditModalOpen(false);
      setParsingSettings([]);
      setMappingError("");
      setParsingMessage("");
      return;
    }

    setMappingLoading(true);
    setMappingError("");

    try {
      const data = await getHotelMapping(Number(hotelId));
      setMappingRows(data);
      const allowedIds = new Set(data.map((row) => row.id));
      setEditedUrlsByRow((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([rowId]) => allowedIds.has(Number(rowId)))),
      );
      setChangedPlatformsByRow((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([rowId]) => allowedIds.has(Number(rowId)))),
      );
    } catch (error) {
      console.error("Не удалось загрузить маппинг объекта", error);
      setMappingRows([]);
      setMappingError("Не удалось загрузить маппинг объекта");
    } finally {
      setMappingLoading(false);
    }
  }

  async function handleRecheckMapping() {
    if (!hasSelectedObject || mappingLoading) return;

    setMappingLoading(true);
    setMappingError("");
    setParsingMessage("");

    try {
      const data = await recheckHotelMapping(Number(object));
      setMappingRows(data);
      setEditedUrlsByRow({});
      setChangedPlatformsByRow({});
      setEditingRow(null);
      setEditModalOpen(false);
    } catch (error) {
      console.error("Не удалось повторно проверить маппинг объекта", error);
      setMappingError("Не удалось повторно проверить маппинг объекта");
    } finally {
      setMappingLoading(false);
    }
  }

  useEffect(() => {
    void loadMapping(object);
  }, [object]);

  const companyOptions = useMemo(
    () =>
      [...companies]
        .sort((a, b) => a.company_name.localeCompare(b.company_name))
        .map((item) => ({ label: item.company_name, value: String(item.id) })),
    [companies],
  );

  const objectOptions = useMemo(
    () => hotels.map((hotel) => ({ label: hotel.name, value: String(hotel.id) })),
    [hotels],
  );

  const selectedHotel = useMemo(
    () => hotels.find((hotel) => String(hotel.id) === object) ?? null,
    [hotels, object],
  );
  const selectedHotelName = selectedHotel?.name ?? "";
  const baseMappingRows = useMemo(
    () => mappingRows.filter((row) => row.is_base === true),
    [mappingRows],
  );
  const competitorMappingRows = useMemo(
    () => mappingRows.filter((row) => row.is_base !== true),
    [mappingRows],
  );

  const hasSelectedObject = object !== "";
  const selectedGroupHasParsing = hasExistingParsing(selectedHotel);
  const canAddParsingSettings = Boolean(hasSelectedObject && selectedHotel?.group_id && !selectedGroupHasParsing);

  function getEffectiveUrls(row: HotelMappingRow): ParsingManageHotelUrls {
    const nextUrls = { ...buildParsingUrls(row) };
    const editedUrls = editedUrlsByRow[row.id];

    if (!editedUrls) return nextUrls;

    for (const field of EDITABLE_PLATFORM_FIELDS) {
      if (!(field.key in editedUrls)) continue;
      const value = editedUrls[field.key];
      if (typeof value === "string" && value) {
        nextUrls[field.key] = value;
      } else {
        delete nextUrls[field.key];
      }
    }

    return nextUrls;
  }

  function getChangedPlatforms(row: HotelMappingRow): EditablePlatformKey[] {
    return Array.from(
      new Set([
        ...getRowChangedPlatforms(row),
        ...(changedPlatformsByRow[row.id] ?? []),
      ]),
    );
  }

  async function handleApprove(competitorId: number) {
    setActionLoadingId(competitorId);
    setActionType("approve");
    setMappingError("");

    try {
      await approveCompetitor(competitorId, true);
      await loadMapping(object);
    } catch (error) {
      console.error("Не удалось одобрить конкурента", error);
      setMappingError("Не удалось одобрить конкурента");
    } finally {
      setActionLoadingId(null);
      setActionType(null);
    }
  }

  async function handleDelete(competitorId: number) {
    const shouldDelete = window.confirm("Удалить запись маппинга?");
    if (!shouldDelete) return;

    setActionLoadingId(competitorId);
    setActionType("delete");
    setMappingError("");

    try {
      await deleteCompetitor(competitorId);
      await loadMapping(object);
    } catch (error) {
      console.error("Не удалось удалить конкурента", error);
      setMappingError("Не удалось удалить конкурента");
    } finally {
      setActionLoadingId(null);
      setActionType(null);
    }
  }

  async function handleSendToParsing() {
    if (!hasSelectedObject || parsingLoading) return;

    if (!selectedHotel?.group_id) {
      setMappingError("У выбранного объекта отсутствует group_id, отправка в парсинг недоступна");
      return;
    }

    if (
      canAddParsingSettings &&
      parsingSettings.some((setting) => !Number.isInteger(setting.min_stay) || setting.min_stay < 1 || setting.min_stay > 10)
    ) {
      setMappingError("min_stay должен быть целым числом от 1 до 10");
      return;
    }

    const competitorsForParsing =
      parsingMode === "approved"
        ? competitorMappingRows.filter((row) => row.approved)
        : competitorMappingRows;
    if (competitorsForParsing.length === 0) {
      setMappingError(
        parsingMode === "approved"
          ? "Нет подтвержденных конкурентов для отправки в парсинг"
          : "Нет конкурентов для отправки в парсинг",
      );
      return;
    }
    const hotelsPayload: ParsingManageHotelItem[] = [
      ...baseMappingRows.slice(0, 1).map((row) => ({
        title: row.name || selectedHotelName || "Без названия",
        main_uid: resolveMainUid(row),
        local_id: row.id,
        urls: getEffectiveUrls(row),
        changed: getChangedPlatforms(row),
        is_base: true,
      })),
      ...competitorsForParsing.map((row) => ({
        title: row.name || "Без названия",
        main_uid: resolveMainUid(row),
        local_id: row.id,
        urls: getEffectiveUrls(row),
        changed: getChangedPlatforms(row),
        is_base: false,
      })),
    ];

    setParsingLoading(true);
    setParsingMessage("");
    setMappingError("");

    try {
      const response = await manageHotels({
        group_id: selectedHotel.group_id,
        hotels: hotelsPayload,
        ...(canAddParsingSettings && parsingSettings.length > 0 ? { parsing_settings: parsingSettings } : {}),
      });
      if (response.status === "error") {
        setMappingError(formatParsingErrors(response.errors) || "Не удалось отправить конкурентов в парсинг");
        return;
      }

      setParsingMessage(
        selectedHotelName
          ? `В парсинг отправлены данные по объекту "${selectedHotelName}" и ${competitorsForParsing.length} ${
              parsingMode === "approved" ? "подтвержденным конкурентам" : "конкурентам"
            }.`
          : `В парсинг отправлены ${competitorsForParsing.length} ${
              parsingMode === "approved" ? "подтвержденных конкурентов" : "конкурентов"
            }.`,
      );
    } catch (error: any) {
      console.error("Не удалось отправить конкурентов в парсинг", error);
      setMappingError(formatManageHotelsError(error, "Не удалось отправить конкурентов в парсинг"));
    } finally {
      setParsingLoading(false);
    }
  }

  async function handleDownloadCsv() {
    if (!hasSelectedObject || csvLoading) return;

    setCsvLoading(true);
    setMappingError("");

    try {
      const { blob, filename } = await downloadHotelMappingCsv(Number(object));
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename || `hotel-mapping-${object}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Не удалось скачать CSV маппинга", error);
      setMappingError("Не удалось скачать CSV маппинга");
    } finally {
      setCsvLoading(false);
    }
  }

  function handleOpenEditModal(row: HotelMappingRow) {
    setEditingRow(row);
    setEditModalOpen(true);
  }

  function handleSaveMappingUrls(
    rowId: number,
    urls: Partial<Record<EditablePlatformKey, string | null>>,
    touchedPlatforms: EditablePlatformKey[],
  ) {
    setEditedUrlsByRow((prev) => ({
      ...prev,
      [rowId]: urls,
    }));

    if (touchedPlatforms.length === 0) return;

    setChangedPlatformsByRow((prev) => ({
      ...prev,
      [rowId]: Array.from(new Set([...(prev[rowId] ?? []), ...touchedPlatforms])),
    }));
  }

  function handleAddParsingSetting() {
    setParsingSettings((current) => [...current, { ...DEFAULT_PARSING_SETTING }]);
    setMappingError("");
  }

  function handleUpdateParsingSetting(index: number, patch: Partial<ParsingManageSetting>) {
    setParsingSettings((current) =>
      current.map((setting, settingIndex) => (settingIndex === index ? { ...setting, ...patch } : setting)),
    );
  }

  function handleRemoveParsingSetting(index: number) {
    setParsingSettings((current) => current.filter((_, settingIndex) => settingIndex !== index));
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="mx-auto w-full max-w-[1480px] px-6 py-10 space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-8 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="absolute inset-y-0 right-0 w-48 translate-x-16 rounded-full bg-gradient-to-b from-indigo-100 to-purple-100 blur-3xl opacity-60 pointer-events-none" />
          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-slate-900">Маппинг</h1>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_10px_28px_rgba(15,23,42,0.08)]">
          <div className="grid gap-4">
            <SelectField
              value={company}
              onChange={(value) => {
                setCompany(value);
                setObject("");
                setMappingRows([]);
                setEditedUrlsByRow({});
                setChangedPlatformsByRow({});
                setParsingSettings([]);
                setMappingError("");
              }}
              placeholder="Выберите компанию"
              icon={<Link2 size={18} />}
              options={companyOptions}
              disabled={companiesLoading}
            />

            {companiesError ? <div className="text-sm text-rose-500">{companiesError}</div> : null}

            <SelectField
              value={object}
              onChange={(value) => {
                setObject(value);
                setEditedUrlsByRow({});
                setChangedPlatformsByRow({});
                setParsingSettings([]);
                setParsingMessage("");
                setEditModalOpen(false);
                setEditingRow(null);
              }}
              placeholder="Выберите объект размещения"
              icon={<Link2 size={18} />}
              options={objectOptions}
              disabled={!company || hotelsLoading}
            />

            {hotelsError ? <div className="text-sm text-rose-500">{hotelsError}</div> : null}
            {mappingError ? <div className="text-sm text-rose-500">{mappingError}</div> : null}
          </div>
        </section>

        {canAddParsingSettings ? (
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_10px_28px_rgba(15,23,42,0.08)]">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Стартовые настройки парсинга</h2>
                <p className="text-sm text-slate-500">
                  Настройки будут отправлены вместе с группой при первом создании парсинга.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddParsingSetting}
                disabled={parsingLoading}
                className="inline-flex items-center gap-2 self-start rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Plus size={16} />
                Добавить настройку
              </button>
            </div>

            {parsingSettings.length === 0 ? (
              <div className="mt-5 rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
                Стартовые настройки не добавлены
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                {parsingSettings.map((setting, index) => (
                  <div key={index} className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-800">Настройка {index + 1}</div>
                      <button
                        type="button"
                        onClick={() => handleRemoveParsingSetting(index)}
                        disabled={parsingLoading}
                        className="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Удалить настройку парсинга"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      <label className="space-y-1 text-sm font-semibold text-slate-700">
                        <span>min_stay</span>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          step={1}
                          value={setting.min_stay}
                          onChange={(event) =>
                            handleUpdateParsingSetting(index, {
                              min_stay: Number(event.target.value),
                            })
                          }
                          disabled={parsingLoading}
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition focus:border-indigo-400 disabled:cursor-not-allowed disabled:opacity-60"
                          required
                        />
                      </label>

                      <label className="space-y-1 text-sm font-semibold text-slate-700">
                        <span>depth</span>
                        <select
                          value={setting.depth}
                          onChange={(event) =>
                            handleUpdateParsingSetting(index, {
                              depth: Number(event.target.value) as ParsingManageDepth,
                            })
                          }
                          disabled={parsingLoading}
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition focus:border-indigo-400 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {DEPTH_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1 text-sm font-semibold text-slate-700">
                        <span>Частота парсинга</span>
                        <select
                          value={setting.interval}
                          onChange={(event) =>
                            handleUpdateParsingSetting(index, {
                              interval: Number(event.target.value) as ParsingManageInterval,
                            })
                          }
                          disabled={parsingLoading}
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition focus:border-indigo-400 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {INTERVAL_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1 text-sm font-semibold text-slate-700">
                        <span>guests</span>
                        <select
                          value={setting.guests}
                          onChange={(event) =>
                            handleUpdateParsingSetting(index, {
                              guests: Number(event.target.value) as ParsingManageGuests,
                            })
                          }
                          disabled={parsingLoading}
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition focus:border-indigo-400 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {GUEST_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : null}

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_10px_28px_rgba(15,23,42,0.08)]">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Таблица маппинга</h2>
              <p className="text-sm text-slate-500">
                {selectedHotelName
                  ? `Данные по объекту: ${selectedHotelName}`
                  : "Выберите объект размещения, чтобы увидеть маппинг"}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center rounded-xl border border-sky-100 bg-sky-50/70 p-0.5 shadow-[0_10px_22px_rgba(14,165,233,0.10)]">
                <button
                  type="button"
                  onClick={() => setParsingMode("all")}
                  className={`rounded-[10px] px-2.5 py-1.5 text-xs font-semibold transition ${
                    parsingMode === "all"
                      ? "bg-gradient-to-r from-sky-600 to-cyan-600 text-white shadow-[0_8px_18px_rgba(14,165,233,0.24)]"
                      : "text-slate-600 hover:bg-white/80"
                  }`}
                >
                  Всех
                </button>
                <button
                  type="button"
                  onClick={() => setParsingMode("approved")}
                  className={`rounded-[10px] px-2.5 py-1.5 text-xs font-semibold transition ${
                    parsingMode === "approved"
                      ? "bg-gradient-to-r from-sky-600 to-cyan-600 text-white shadow-[0_8px_18px_rgba(14,165,233,0.24)]"
                      : "text-slate-600 hover:bg-white/80"
                  }`}
                >
                  Подтвержденные
                </button>
              </div>

              <button
                type="button"
                onClick={() => void handleDownloadCsv()}
                disabled={!hasSelectedObject || mappingLoading || csvLoading}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-[0_10px_22px_rgba(15,23,42,0.08)] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {csvLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {csvLoading ? "Скачиваем..." : "Скачать CSV"}
              </button>

              <button
                type="button"
                onClick={() => void handleSendToParsing()}
                disabled={!hasSelectedObject || mappingLoading || parsingLoading || csvLoading}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-sky-600 to-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(14,165,233,0.22)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {parsingLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {parsingLoading ? "Отправляем..." : "Отправить в парсинг"}
              </button>

              <button
                type="button"
                onClick={() => void handleRecheckMapping()}
                disabled={!hasSelectedObject || mappingLoading}
                className="inline-flex items-center gap-2 rounded-2xl border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-600 shadow-[0_10px_22px_rgba(99,102,241,0.12)] transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw size={16} />
                Проверить повторно
              </button>
            </div>
          </div>

          {parsingMessage ? (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {parsingMessage}
            </div>
          ) : null}

          <div className="mt-6 space-y-8">
            <MappingTable
              title="Базовый объект"
              subtitle="Сам объект размещения проверяется в эталонной базе отдельно и не смешивается с конкурентами."
              rows={baseMappingRows}
              loading={mappingLoading}
              hasSelectedObject={hasSelectedObject}
              emptyMessage="Базовый объект не найден в ответе мапинга"
              showStatus={false}
              getEffectiveUrls={getEffectiveUrls}
              onEdit={handleOpenEditModal}
            />

            <MappingTable
              title="Конкуренты"
              subtitle="В этой таблице остаются только записи конкурентов."
              rows={competitorMappingRows}
              loading={mappingLoading}
              hasSelectedObject={hasSelectedObject}
              emptyMessage="По выбранному объекту конкуренты в мапинге не найдены"
              actionsColumn
              actionLoadingId={actionLoadingId}
              actionType={actionType}
              getEffectiveUrls={getEffectiveUrls}
              onEdit={handleOpenEditModal}
              onApprove={handleApprove}
              onDelete={handleDelete}
            />
          </div>
        </section>
      </main>

      <EditMappingModal
        open={editModalOpen}
        row={editingRow}
        initialUrls={editingRow ? getEffectiveUrls(editingRow) : {}}
        onClose={() => {
          setEditModalOpen(false);
          setEditingRow(null);
        }}
        onSave={handleSaveMappingUrls}
      />
    </div>
  );
}

function FlagCell({ url }: { url?: string }) {
  const hasUrl = Boolean(url);
  const className = [
    "mx-auto inline-flex h-9 w-11 items-center justify-center rounded-2xl transition",
    hasUrl ? "bg-emerald-50 text-emerald-500 hover:bg-emerald-100" : "bg-rose-50 text-rose-400",
  ].join(" ");

  if (hasUrl && url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={className}
        aria-label="Открыть ссылку ОТА"
        title="Открыть ссылку"
      >
        <Check size={18} />
      </a>
    );
  }

  return (
    <span className={className}>
      <X size={18} />
    </span>
  );
}

function StatusPill({ approved }: { approved: boolean | null }) {
  return (
    <span
      className={[
        "inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold",
        approved ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500",
      ].join(" ")}
    >
      {approved ? "Подтвержден" : "Не подтвержден"}
    </span>
  );
}

type MappingTableProps = {
  title: string;
  subtitle: string;
  rows: HotelMappingRow[];
  loading: boolean;
  hasSelectedObject: boolean;
  emptyMessage: string;
  showStatus?: boolean;
  actionsColumn?: boolean;
  actionLoadingId?: number | null;
  actionType?: "approve" | "delete" | null;
  getEffectiveUrls: (row: HotelMappingRow) => ParsingManageHotelUrls;
  onEdit?: (row: HotelMappingRow) => void;
  onApprove?: (competitorId: number) => Promise<void>;
  onDelete?: (competitorId: number) => Promise<void>;
};

function MappingTable({
  title,
  subtitle,
  rows,
  loading,
  hasSelectedObject,
  emptyMessage,
  showStatus = true,
  actionsColumn = false,
  actionLoadingId = null,
  actionType = null,
  getEffectiveUrls,
  onEdit,
  onApprove,
  onDelete,
}: MappingTableProps) {
  const hasActions = Boolean(actionsColumn || onEdit);
  const colSpan = 2 + PLATFORM_COLUMNS.length + (showStatus ? 1 : 0) + (hasActions ? 1 : 0);

  return (
    <div className="rounded-3xl border border-slate-100 bg-slate-50/50 p-5">
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
        <span className="inline-flex items-center self-start rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-500">
          {loading ? "Загрузка..." : `${rows.length} записей`}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[1120px] w-full border-separate border-spacing-y-3 text-sm">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="px-4 py-2">ID</th>
              <th className="px-4 py-2">Название / Ссылка клиента</th>
              {showStatus ? <th className="px-4 py-2">Статус</th> : null}
              {PLATFORM_COLUMNS.map((column) => (
                <th key={column.key} className="px-4 py-2 text-center">
                  {column.label}
                </th>
              ))}
              {hasActions ? <th className="px-4 py-2 text-right">Действия</th> : null}
            </tr>
          </thead>

          <tbody>
            {!hasSelectedObject ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-slate-400">
                  Выберите объект размещения
                </td>
              </tr>
            ) : null}

            {hasSelectedObject && loading ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-slate-400">
                  Загрузка маппинга...
                </td>
              </tr>
            ) : null}

            {hasSelectedObject && !loading && rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-slate-400">
                  {emptyMessage}
                </td>
              </tr>
            ) : null}

            {!loading &&
              rows.map((row) => {
                const effectiveUrls = getEffectiveUrls(row);
                const isApprovePending = actionLoadingId === row.id && actionType === "approve";
                const isDeletePending = actionLoadingId === row.id && actionType === "delete";

                return (
                  <tr key={`${title}-${row.id}`} className="rounded-2xl bg-white/90">
                    <td className="px-4 py-3 font-semibold text-slate-700">{row.id}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{row.name || "Без названия"}</div>
                      <a
                        href={row.url}
                        className="break-all text-indigo-500 hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.url}
                      </a>
                    </td>

                    {showStatus ? (
                      <td className="px-4 py-3">
                        <StatusPill approved={row.approved} />
                      </td>
                    ) : null}

                    {PLATFORM_COLUMNS.map((column) => (
                      <td key={column.key} className="px-4 py-3 text-center">
                        <FlagCell url={effectiveUrls[column.key]} />
                      </td>
                    ))}

                    {hasActions ? (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {onEdit ? (
                            <button
                              type="button"
                              onClick={() => onEdit(row)}
                              disabled={isDeletePending || isApprovePending}
                              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                              aria-label="Редактировать маппинг"
                            >
                              <Pencil size={18} />
                            </button>
                          ) : null}

                          {actionsColumn ? (
                            <button
                              type="button"
                              onClick={() => void onDelete?.(row.id)}
                              disabled={isDeletePending || isApprovePending}
                              className="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                              aria-label="Удалить"
                            >
                              {isDeletePending ? (
                                <Loader2 size={18} className="animate-spin" />
                              ) : (
                                <Trash2 size={18} />
                              )}
                            </button>
                          ) : null}

                          {actionsColumn ? (
                            <button
                              type="button"
                              onClick={() => void onApprove?.(row.id)}
                              disabled={isApprovePending || isDeletePending}
                              className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 p-2 text-white shadow-[0_12px_24px_rgba(16,185,129,0.35)] transition hover:translate-y-[-1px] disabled:cursor-not-allowed disabled:opacity-50"
                              aria-label="Одобрить"
                            >
                              {isApprovePending ? (
                                <Loader2 size={18} className="animate-spin" />
                              ) : (
                                <CircleCheck size={18} />
                              )}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
